/**
 * Tool Configuration Utility
 *
 * Centralized configuration for tool names, descriptions, and emojis.
 */
import type { LucideIcon } from 'lucide-react'
import {
  Code,
  Database,
  Eye,
  FileText,
  Lock,
  Package,
  Search,
  SquarePen,
  Wrench,
} from 'lucide-react'
import { TOOL_DETAIL_MAX_CHARS } from '@/config/constants'

export type ToolAction =
  | 'query'
  | 'read'
  | 'preview'
  | 'change'
  | 'code'
  | 'load'
  | 'search'
  | 'other'

// Tool name mappings with user-friendly verb-noun patterns, emojis, and
// (optionally) a Lucide icon for the tool rows. Tools without an icon keep
// the Search icon that every row rendered before per-tool icons existed.
const toolMappings: Record<
  string,
  {
    running: string
    done: string
    emoji: string
    action: ToolAction
    icon?: LucideIcon
  }
> = {
  execute_sql: {
    running: 'Running database query...',
    done: 'Ran database query',
    emoji: '🔍',
    action: 'query',
  },
  describe_table: {
    running: 'Checking data structure...',
    done: 'Found data structure',
    emoji: '📊',
    action: 'read',
  },
  get_tables: {
    running: 'Finding available data...',
    done: 'Found available data',
    emoji: '📋',
    action: 'read',
  },
  search_tables: {
    running: 'Searching for data...',
    done: 'Found matching data',
    emoji: '🔎',
    action: 'search',
  },
  get_table_columns: {
    running: 'Checking data fields...',
    done: 'Found data fields',
    emoji: '🏗️',
    action: 'read',
  },
  calc: {
    running: 'Doing calculations...',
    done: 'Finished calculations',
    emoji: '🧮',
    action: 'other',
  },
  get_current_datetime: {
    running: 'Getting current time...',
    done: 'Found current time',
    emoji: '🕐',
    action: 'other',
  },
  calculate_date: {
    running: 'Calculating dates...',
    done: 'Calculated dates',
    emoji: '📅',
    action: 'other',
  },
  get_version: {
    running: 'Checking system info...',
    done: 'Found system info',
    emoji: '⚙️',
    action: 'other',
  },
  show_structured: {
    running: 'Organizing results...',
    done: 'Organized results',
    emoji: '📝',
    action: 'other',
  },

  // Capability loading
  load_capability: {
    running: 'Loading capability...',
    done: 'Loaded capability',
    emoji: '📦',
    action: 'load',
    icon: Package,
  },
  search_tools: {
    running: 'Searching for tools...',
    done: 'Found matching tools',
    emoji: '🛠️',
    action: 'search',
    icon: Wrench,
  },

  // Duty management
  get_duty: {
    running: 'Reading duty...',
    done: 'Read duty',
    emoji: '📄',
    action: 'read',
    icon: FileText,
  },

  // Billing (Careforce global admins only). The same call previews and writes,
  // so the title does not say a credit was added.
  add_billing_credit: {
    running: 'Add Billing Credit...',
    done: 'Add Billing Credit',
    emoji: '💳',
    action: 'change',
    icon: Lock,
  },
  get_billing_rate_card: {
    running: 'Reading rate card...',
    done: 'Read rate card',
    emoji: '💳',
    action: 'read',
    icon: FileText,
  },

  // Code execution
  run_code: {
    running: 'Running analysis code...',
    done: 'Ran analysis code',
    emoji: '🐍',
    action: 'code',
  },

  // Logfire MCP tools
  logfire_arbitrary_query: {
    running: 'Querying Logfire logs...',
    done: 'Queried Logfire logs',
    emoji: '🔥',
    action: 'query',
  },
  logfire_find_exceptions_in_file: {
    running: 'Finding exceptions...',
    done: 'Found exceptions',
    emoji: '⚠️',
    action: 'search',
  },
  logfire_logfire_link: {
    running: 'Creating Logfire link...',
    done: 'Created Logfire link',
    emoji: '🔗',
    action: 'other',
  },
  logfire_schema_reference: {
    running: 'Getting Logfire schema...',
    done: 'Got Logfire schema',
    emoji: '📐',
    action: 'read',
  },
}

// Default fallback emoji for unknown tools
const DEFAULT_TOOL_EMOJI = '🔧'

export function convertToolName(
  toolName: string,
  isDone: boolean = false
): string {
  if (!toolName || typeof toolName !== 'string') {
    return toolName
  }

  // Check if we have a specific mapping
  if (toolMappings[toolName]) {
    return isDone ? toolMappings[toolName].done : toolMappings[toolName].running
  }

  // Fallback: snake_case to Title Case
  const titleCase = toolName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

  return isDone ? titleCase : titleCase + '...'
}

export function getToolEmoji(toolName: string): string {
  if (!toolName || typeof toolName !== 'string') {
    return DEFAULT_TOOL_EMOJI
  }

  // Return specific emoji if we have mapping, otherwise return default
  return toolMappings[toolName]?.emoji || DEFAULT_TOOL_EMOJI
}

const DEFAULT_TOOL_ICON = Search

export function getToolIcon(toolName: string): LucideIcon {
  if (!toolName || typeof toolName !== 'string') {
    return DEFAULT_TOOL_ICON
  }

  return toolMappings[toolName]?.icon || DEFAULT_TOOL_ICON
}

const ACTION_PREFIXES: [string, ToolAction][] = [
  ['get_', 'read'],
  ['list_', 'read'],
  ['read_', 'read'],
  ['describe_', 'read'],
  ['preview_', 'preview'],
  ['dry_run_', 'preview'],
  ['validate_', 'preview'],
  ['update_', 'change'],
  ['manage_', 'change'],
  ['save_', 'change'],
  ['apply_', 'change'],
  ['add_', 'change'],
  ['purchase_', 'change'],
  ['setup_', 'change'],
  ['teardown_', 'change'],
  ['submit_', 'change'],
  ['approve_', 'change'],
  ['search_', 'search'],
]

export function getToolAction(toolName: string): ToolAction {
  const mapped = toolMappings[toolName]?.action
  if (mapped) return mapped
  return (
    ACTION_PREFIXES.find(([prefix]) => toolName.startsWith(prefix))?.[1] ??
    'other'
  )
}

export const TOOL_ACTIONS: Record<
  ToolAction,
  { icon: LucideIcon; verb: string; noun: string; nounPlural: string }
> = {
  query: { icon: Database, verb: 'ran', noun: 'query', nounPlural: 'queries' },
  read: { icon: FileText, verb: 'read', noun: 'record', nounPlural: 'records' },
  preview: {
    icon: Eye,
    verb: 'previewed',
    noun: 'change',
    nounPlural: 'changes',
  },
  change: {
    icon: SquarePen,
    verb: 'made',
    noun: 'change',
    nounPlural: 'changes',
  },
  code: { icon: Code, verb: 'ran', noun: 'script', nounPlural: 'scripts' },
  load: {
    icon: Package,
    verb: 'loaded',
    noun: 'capability',
    nounPlural: 'capabilities',
  },
  search: {
    icon: Search,
    verb: 'ran',
    noun: 'search',
    nounPlural: 'searches',
  },
  other: { icon: Wrench, verb: 'used', noun: 'tool', nounPlural: 'tools' },
}

// Allowlist, not a generic `name`/`title` scan: on lookups those args can be a
// person, who would then show on the collapsed row.
const TOOL_DETAIL_ARG: Record<string, string> = {
  load_capability: 'id',
  describe_table: 'table_name',
  search_tables: 'pattern',
  get_duty_template: 'name',
  save_diagram: 'name',
  check_organization_name: 'name',
  generate_simple_pdf: 'title',
  generate_report_pdf: 'title',
}

function truncate(text: string): string {
  return text.length > TOOL_DETAIL_MAX_CHARS
    ? `${text.slice(0, TOOL_DETAIL_MAX_CHARS - 1)}…`
    : text
}

/** One short line describing what a tool call acted on, from its args. */
export function getToolDetail(
  toolName: string,
  args: Record<string, unknown> | undefined
): string {
  if (!args) return ''
  if (typeof args.sql === 'string') {
    const line = args.sql
      .split('\n')
      .map((l) => l.trim())
      .find((l) => l && !l.startsWith('--'))
    return truncate(line ?? '')
  }
  const key = TOOL_DETAIL_ARG[toolName]
  const value = key ? args[key] : undefined
  return typeof value === 'string' ? truncate(value) : ''
}

/**
 * Hoku tools report failure as `{"success": false, "error": ...}` or a bare
 * `{"error": ...}` with no `success` key. Persisted
 * results arrive JSON-encoded twice (the tool's own dump, then the history
 * serializer's), so unwrap up to two layers before looking.
 */
export function toolResultStatus(
  result: string | undefined
): 'completed' | 'error' {
  let value: unknown = result
  for (let i = 0; i < 2 && typeof value === 'string'; i++) {
    try {
      value = JSON.parse(value)
    } catch {
      break
    }
  }
  if (typeof value !== 'object' || value === null) return 'completed'
  const { success, error } = value as { success?: unknown; error?: unknown }
  const failed =
    success === false ||
    (success !== true && typeof error === 'string' && error !== '')
  return failed ? 'error' : 'completed'
}
